import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { EmployeeService } from "../../../../../../services/employee.service";
import { LogService } from "../../../../../../services/log.service";
import firebase from "firebase/compat/app";
import { ManagementMOM, ManagementMOMItems } from "../../counsellors-mom.model";
import { ManagementMomService } from "../../management-mom.service";
import { AddParticipantsDialogComponent } from "../../../../../education/components/education-mom/add-participants-dialog/add-participants-dialog.component";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

/**
 * Component for adding/editing Management MOM (Minutes of Meeting) sessions
 * Includes a table for multiple session items with topic and status
 */
@Component({
  selector: 'app-add-mom-management',
  templateUrl: './add-mom-management.component.html',
  styleUrls: ['./add-mom-management.component.scss']
})
export class AddMomManagementComponent implements OnInit, OnDestroy {

  private _destroyed$ = new Subject<void>();

  momForm!: FormGroup;
  allManagementMembers: any[] = [];
  filteredManagementMembers: any[] = [];
  participantSearch: string = '';
  currentEditId: any;
  isLoading: boolean = false;
  isAdmin: boolean = false; // Control visibility of admin-only fields

  // Table columns for session items
  displayedColumns: string[] = ['topic', 'remark', 'actions'];

  // Data source for the table
  sessionDataSource: any[] = [];

  // Status options
  statusOptions: Array<'DONE' | 'PENDING' | 'ONGOING'> = ['PENDING', 'ONGOING', 'DONE'];
  displayParticipants = (c: any) => c;

  constructor(
    private fb: FormBuilder,
    private managementMomService: ManagementMomService,
    private employeeService: EmployeeService,
    private mLogService: LogService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<AddMomManagementComponent>
  ) {}

  ngOnInit(): void {
    this.formInit();
    this.fetchAllManagementMembers();

    // Check if user is admin (Super Admin or Kavita Shetty)
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const username = userData.userName || '';

    this.isAdmin = username === 'Master Admin' || username === 'Kavita Shetty';

    // Get ID from dialog data for edit mode
    this.currentEditId = this.data?.id || null;

    if (this.data) {
      const record = this.data;

      // Patch basic fields
      this.momForm.patchValue({
        date: record.date?.toDate?.() || record.date,

        participants: record.participants || [],
        remark: record.remark || ''
      });

      // Patch session items
      if (record.sessions && record.sessions.length > 0) {
        this.sessions.clear();
        record.sessions.forEach((session: ManagementMOMItems) => {
          this.sessions.push(this.createSessionItem(session));
        });
        this.updateDataSource();
      }
    }
  }

  /**
   * Initialize the form with validation
   */
  formInit() {
    this.momForm = this.fb.group({
      date: ['', Validators.required],
      participants: [[], Validators.required],
      remark: [''],
      sessions: this.fb.array([])
    });
  }

  /**
   * Get sessions FormArray
   */
  get sessions(): FormArray {
    return this.momForm.get('sessions') as FormArray;
  }

  /**
   * Create a new session item form group
   */
  createSessionItem(session?: ManagementMOMItems): FormGroup {
    return this.fb.group({
      topic: [session?.topic || '', Validators.required],
      remark: [session?.remark || 'PENDING', Validators.required]
    });
  }

  /**
   * Add a new session row to the table
   */
  addSessionRow() {
    this.sessions.push(this.createSessionItem());
    this.updateDataSource();
  }

  /**
   * Remove a session row from the table
   */
  removeSessionRow(index: number) {
    console.log('Removing row at index:', index);
    console.log('Sessions before:', this.sessions.length);
    this.sessions.removeAt(index);
    console.log('Sessions after:', this.sessions.length);
    this.updateDataSource();
  }

  /**
   * Update the table data source
   */
  private updateDataSource() {
    this.sessionDataSource = [...this.sessions.controls];
    console.log('Data source updated:', this.sessionDataSource.length);
  }

  /**
   * Submit the form to create or update a management MOM session
   */
  submitForm() {
    if (this.momForm.invalid) {
      this.snackBar.open('Please fill all required fields', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.sessions.length === 0) {
      this.snackBar.open('Please add at least one session item', 'Close', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isLoading = true;

    // Extract User Info From Local Storage
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const username = `${userData.userName || ''}`.trim() || 'Unknown User';

    const formValue = this.momForm.value;

    const payload: ManagementMOM = {
      date: new Date(formValue.date),

      participants: formValue.participants,
      sessions: formValue.sessions,
      remark: formValue.remark
    };

    if (this.currentEditId) {
      payload.updatedBy = username;
      payload.updatedAt = firebase.firestore.FieldValue.serverTimestamp();

      this.managementMomService.update(this.currentEditId, payload)
        .then(() => {
          this.mLogService.addLog({
            date: Date.now(),
            section: 'Management MOM Session',
            action: 'Updated',
            description: `${username} updated the Management MOM Session`,
            currentIp: localStorage.getItem('currentip')!
          });

          this.afterSave('Updated successfully!');
        })
        .catch((error: any) => {
          this.isLoading = false;
          this.snackBar.open('Error updating session: ' + error.message, 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        });
    } else {
      payload.createdBy = username;
      payload.createdAt = firebase.firestore.FieldValue.serverTimestamp();
      payload.status = "Active";

      this.managementMomService.create(payload)
        .then(() => {
          this.mLogService.addLog({
            date: Date.now(),
            section: 'Management MOM Session',
            action: 'Created',
            description: `${username} created a new Management MOM Session`,
            currentIp: localStorage.getItem('currentip')!
          });

          this.afterSave('Saved successfully!');
        })
        .catch((error: any) => {
          this.isLoading = false;
          this.snackBar.open('Error creating session: ' + error.message, 'Close', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          });
        });
    }
  }

  /**
   * Handle post-save actions
   */
  private afterSave(message: string) {
    this.isLoading = false;
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
    this.dialogRef.close(this.currentEditId ? 'updated' : 'saved');
    this.momForm.reset();
    this.currentEditId = null;
  }

  /**
   * Fetch all management members using the employee service
   */
  fetchAllManagementMembers() {
    this.employeeService.getManagementAndCounsellors()
      .pipe(takeUntil(this._destroyed$))
      .subscribe(members => {
        this.allManagementMembers = members.map(member => member.userName);
        this.filteredManagementMembers = [...this.allManagementMembers];
      });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /**
   * Handle participant search input change
   */
  onParticipantSearchChange(event: Event): void {
    this.participantSearch = (event.target as HTMLInputElement).value;
    this.filterParticipants();
  }

  /**
   * Filter management members based on search input
   */
  filterParticipants(): void {
    const searchValue = this.participantSearch.toLowerCase().trim();
    let results = !searchValue
      ? [...this.allManagementMembers]
      : this.allManagementMembers.filter(member =>
        member.toLowerCase().includes(searchValue)
      );

    const selected: string[] =
      this.momForm.get('participants')?.value || [];

    selected.forEach(value => {
      if (!results.includes(value)) {
        results.push(value);
      }
    });

    this.filteredManagementMembers = results;
  }

  /**
   * Cancel and close the dialog
   */
  cancel() {
    this.dialogRef.close();
  }

  /**
   * Open dialog to add participants from all employees
   */
  openParticipantsDialog(): void {
    const currentParticipants = this.momForm.get('participants')?.value || [];

    const dialogRef = this.dialog.open(AddParticipantsDialogComponent, {
      width: '700px',
      maxHeight: '90vh',
      data: { currentParticipants: [] }  // Education dialog expects objects with name/email, but we'll convert
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.length > 0) {
        // Extract just the names from the result (which are objects with name and email)
        const participantNames = result.map((p: any) => p.name);

        // Merge with existing participants
        const existingSet = new Set(this.allManagementMembers);
        const newParticipants = participantNames.filter((name: string) => !existingSet.has(name));

        // Add new participants to allManagementMembers
        this.allManagementMembers = [...this.allManagementMembers, ...newParticipants].sort((a, b) => a.localeCompare(b));

        // Update filtered list
        this.filteredManagementMembers = [...this.allManagementMembers];

        // Merge with current selection
        const currentSelection = this.momForm.get('participants')?.value || [];
        const mergedSelection = Array.from(new Set([...currentSelection, ...participantNames]));

        // Update form control with merged participants
        this.momForm.patchValue({ participants: mergedSelection });
      }
    });
  }

  onParticipantsChange(selected: any[]) {
    this.momForm.get('participants')?.setValue(selected);
  }
}


